import React, { FC, useMemo } from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Education, ProfessionalExperience, References } from '../../../models/UserModels';
import moment from 'moment';
import { PdfHtml } from '../PdfHtml';
import config from '../../../config';
import Utils from '../../../utils/Utils';
import QRCode from 'qrcode';
import { CvTemplateProfile, ProfileActivity } from '../CvTemplateModal';
import { OrganizationView } from '../TemplateOrganizationComp';
import { VolunteeringView } from '../TemplateVolunteeringComp';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getReactPdfFont());
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getOpenSansFont());
Font.registerHyphenationCallback((word: string) => {
  const urlIndex = word.indexOf('://');
  if (urlIndex !== -1) {
    const schema = word.substring(0, urlIndex + 3);
    const urlText = word
      .substring(urlIndex + 3)
      .split('/')
      .map((v, i) => (i === 0 ? `${schema}${v}` : v));
    return urlText
      .map((v, i) => (i === urlText.length - 1 ? v : `${v}/`))
      .flatMap((v) => v.match(/(.{1,18})/g))
      .filter((v) => !!v) as string[];
  }
  return (word.match(/(.{1,18})/g) as string[]).filter((v) => !!v) as string[];
});
const colors = (color: string) => {
  let color1;
  let color2;
  if (color === '#080808') {
    color1 = '#1A1A1A';
    color2 = '#2B2B2B';
  }
  if (color === '#042357') {
    color1 = '#1D3865';
    color2 = '#344869';
  }
  if (color === '#022715') {
    color1 = '#4C6559';
    color2 = '#667B71';
  }
  if (color === '#333434') {
    color1 = '#505050';
    color2 = '#6F6F6F';
  }
  return { color1, color2 };
};
const pageStyles = (props?: any) =>
  StyleSheet.create({
    body: {
      // flex: 1,
      // flexDirection: 'row',
    },
    title: {
      fontSize: 22,
      color: props?.color ?? '#000',
      fontFamily: 'OpenSans',
      fontWeight: 'bold',
      paddingBottom: 10,
      flexWrap: 'wrap',
    },
    author: {
      fontSize: 12,
      textAlign: 'center',
      marginBottom: 40,
    },
    subtitle: {
      fontSize: 18,
      margin: 12,
      fontFamily: 'Inter',
    },
    name: {
      fontSize: 32,
      color: '#F1FAFF',
      fontFamily: 'Inter',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    designation: {
      fontSize: 16,
      color: '#F1FAFF',
      fontFamily: 'Inter',
      paddingTop: 1,
      fontWeight: 'medium',
    },
    country: {
      fontSize: 14,
      color: '#F1FAFF',
      fontFamily: 'Inter',
      paddingTop: 1,
      fontWeight: 400,
    },
    text: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1.5,
      color: props?.color ? colors(props.color).color2 : '#2B2B2B',
      fontFamily: 'Inter',
    },
    textInstitute: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1.5,
      color: '#4F4F4F',
      fontFamily: 'Inter',
    },
    linkTitle: {
      fontSize: 12,
      color: '#F1FAFF',
      fontFamily: 'Inter',
      textAlign: 'left',
      fontWeight: 'medium',
    },
    linkText: {
      fontSize: 12,
      textAlign: 'left',
      lineHeight: 1.5,
      color: '#F1FAFF',
      fontFamily: 'Inter',
    },
    contactText: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1,
      color: '#F1FAFF',
      fontFamily: 'Inter',
      paddingRight: 2,
    },
    emailText: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1,
      color: '#F1FAFF',
      fontFamily: 'Inter',
      paddingRight: 2,
      textDecoration: 'none',
    },
    descText: {
      fontSize: 10,
      textAlign: 'left',
      color: '#5F5F5F',
      fontFamily: 'Inter',
    },
    image: {
      marginVertical: 15,
      marginHorizontal: 100,
    },
    header: {
      fontSize: 12,
      marginBottom: 20,
      textAlign: 'center',
      color: 'grey',
    },
    footerView: {
      height: 29,
      borderTopLeftRadius: 15,
      borderTopRightRadius: 15,
      backgroundColor: '#F00D0D',
    },
    staticContent: {
      position: 'absolute',
      fontSize: 12,
      bottom: 30,
      left: 100,
      right: 0,
      textAlign: 'center',
      color: '#241313',
    },
    headerBar: {
      // position: 'absolute',
      fontSize: 12,
      top: 15,
      left: 0,
      right: 0,
      height: 10,
      textAlign: 'center',
      color: 'grey',
    },
    colomn2: {
      backgroundColor: '#FFFFFF',
      flex: 2,
      marginTop: 30,
      paddingHorizontal: 22,
    },
    colomn1: {
      backgroundColor: 'transparent',
      flex: 1,
      paddingHorizontal: 22,
      marginLeft: 10,
      marginTop: 10,
      marginBottom: 10,
      borderRadius: 15,
      zIndex: 900,
      maxHeight: '100%',
    },
    dashedBorder: {
      width: 2,
      height: '38.5%',
      borderRightWidth: 1,
      transform: 'scale(1, 4)',
      borderRightStyle: 'dashed',
      borderRightColor: '#AEAEAE',
      position: 'absolute',
      top: 0,
      left: 215,
    },
    durationBox: {
      backgroundColor: props?.color ?? '#042357',
      width: 118,
      height: 19,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dateText: {
      color: '#FFF',
      fontSize: 10,
    },
    timeline: {
      color: '#5F5F5F',
      fontSize: 10,
      fontFamily: 'Inter',
    },
    exTitle: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1.5,
      color: props?.color ? colors(props.color).color1 : '#1A1A1A',
      fontFamily: 'OpenSans',
      paddingTop: 6,
      fontWeight: 'bold',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginTop: 5,
      color: '#F1FAFF',
    },
    tag: {
      backgroundColor: '#CDD1D3',
      minHeight: 22,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 8,
      padding: 5,
      marginRight: 10,
      marginBottom: 8,
    },
    headerContent: {
      // flex: 1,
      width: '100%',
      minHeight: 150,
      backgroundColor: props.color ?? '#002D53',
      color: '#F1FAFF',
      justifyContent: 'center',
      padding: 30,
      justifyItems: 'center',
      alignItems: 'center',
    },
    footerShape: {
      width: 200,
      height: 50,
      position: 'absolute',
      left: 0,
      bottom: 10,
    },
    footerBorder: {
      position: 'absolute',
      fontSize: 12,
      bottom: -5,
      left: -5,
      height: 80,
      width: 240,
      backgroundColor: 'transparent',
      borderColor: 'white',
      borderWidth: 15,
      borderTopWidth: 0,
      borderBottomLeftRadius: 30,
      borderBottomRightRadius: 30,
    },
    topBorder: {
      position: 'absolute',
      fontSize: 12,
      top: -5,
      left: -5,
      height: 80,
      width: 240,
      backgroundColor: 'transparent',
      borderColor: 'white',
      borderWidth: 15,
      borderBottomWidth: 0,
      borderTopLeftRadius: 30,
      borderTopRightRadius: 30,
      zIndex: 1000,
    },
    topBorderImage: {
      marginLeft: -32,
      marginTop: -10,
      width: 230,
    },
    sideBar: {
      position: 'absolute',
      top: 10,
      left: 10,
      bottom: 10,
      width: 210,
      borderRadius: 15,
      backgroundColor: '#E5EAEC',
    },
    emailContainer: {
      flexDirection: 'column',
    },
    contacts: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 0,
    },
    refName: {
      fontSize: 16,
      color: props?.color ? colors(props.color).color1 : '#1A1A1A',
      fontFamily: 'Inter',
      paddingTop: 8,
      marginBottom: 4,
      fontWeight: 'medium',
    },
    boldText: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1.5,
      fontWeight: 'bold',
      color: props?.color ? colors(props.color).color2 : '#2B2B2B',
      fontFamily: 'Inter',
    },
  });

// Create Document Component
type Props = {
  selectedColor?: string;
  profile: CvTemplateProfile;
};
const ExpCard: FC<{ value: ProfessionalExperience; selectedColor?: string }> = ({
  value,
  selectedColor,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  console.log('description............', value.description);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      <Text style={styles.exTitle}>{value.designation.value}</Text>
      <Text style={styles.text}>{`${value.company.value ?? ''} | ${value.city ?? ''} ${
        value.city ? ',' : ''
      } ${value.country ?? ''}`}</Text>
      <View style={{ marginBottom: 5 }}>
        <Text style={styles.timeline}>{duration}</Text>
      </View>
      {value.description && (
        <PdfHtml level={2}>
          {value.description.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
        </PdfHtml>
      )}
    </View>
  );
};
const EduCard: FC<{ value: Education; selectedColor?: string }> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const { type, results, institute, achievements, title, description } = value;
  const duration = useMemo(() => {
    const from = moment(value.period?.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period?.to);
    const toStr = value?.period?.to
      ? to.format('MMM YYYY')
      : type === 'Certification'
      ? 'None'
      : 'Present';
    return [`${fromStr} - ${toStr}`, fromStr, toStr];
  }, [type, value?.period?.from, value?.period?.to]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      {/*<Text style={styles.exTitle}>*/}
      {/*  {type === 'Certification' ? results[0]?.subject?.value ?? type : type}*/}
      {/*</Text>*/}
      <View>
        {type === 'Certification' ? (
          <>
            <Text style={styles.exTitle}>{`${results[0]?.subject?.value ?? type}`}</Text>
            {/*{results[0]?.result ? (*/}
            {/*  <Text style={styles.text}>{`Certification ID : ${results[0]?.result}`}</Text>*/}
            {/*) : (*/}
            {/*  <></>*/}
            {/*)}*/}
          </>
        ) : type === 'GCE A/L' || type === 'GCE O/L' ? (
          <>
            <Text style={styles.exTitle}>{type}</Text>
            {results.map((r, i) => (
              <>
                <Text key={i} style={styles.text}>{`${r.subject?.value} ${
                  r.result ? `(${r.result})` : ''
                }`}</Text>
              </>
            ))}
          </>
        ) : type === 'Other' ? (
          <>
            <Text style={styles.exTitle}>{title ?? ''}</Text>
            {results.map((r, i) => (
              <>
                <Text key={i} style={styles.text}>{`${r.subject?.value} ${
                  r.result ? `(${r.result})` : ''
                }`}</Text>
              </>
            ))}
          </>
        ) : (
          results.map((r, i) => (
            <Text key={i} style={[styles.exTitle, { paddingTop: 5 }]}>{`${r.subject?.value}${
              r.result ? `, ${r.result}` : ''
            }`}</Text>
          ))
        )}
      </View>
      {institute && <Text style={styles.textInstitute}>{institute.value}</Text>}
      {type !== 'Certification' && <Text style={styles.timeline}>{duration[0]}</Text>}
      {type === 'Certification' && value?.period && (
        <Text style={styles.timeline}>{duration[1]}</Text>
      )}
      {description && type === 'Other' && (
        <View style={{ marginTop: 4 }}>
          {/*<Text style={styles.text}>Activities and Societies</Text>*/}
          <PdfHtml level={2}>
            {description.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
      {achievements && (
        <View style={{ marginTop: 4 }}>
          {/*<Text style={styles.text}>Activities and Societies</Text>*/}
          <PdfHtml level={2}>
            {achievements.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
    </View>
  );
};
// const ActivitySocityView: FC<{ value: Education; selectedColor?: string; title?: boolean }> = ({
//   value,
//   selectedColor,
//   title,
// }) => {
//   const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
//   const { institute, description } = value;
//   return (
//     <View wrap={false} style={{ marginTop: 1 }}>
//       {title && (
//         <>
//           <Text style={styles.title}>Activities &</Text>
//           <Text style={styles.title}>Societies</Text>
//         </>
//       )}
//       {institute && description && <Text style={styles.exTitle}>{institute}</Text>}
//       {description && (
//         <PdfHtml>{description.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}</PdfHtml>
//       )}
//     </View>
//   );
// };
const ActivitySocityView: FC<{
  value: ProfileActivity[];
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const activity = value ?? [];
  const data = activity.map((item, index) => {
    return (
      <View key={index} style={{ marginTop: 10 }}>
        {index === 0 && <Text style={styles.title}>Activities & Societies</Text>}
        {item.institute && item.description && (
          <Text style={[styles.exTitle, { marginTop: 20 }]}>{item.institute}</Text>
        )}

        {item.description.map((v) => {
          if (v) {
            return (
              <PdfHtml key={v}>
                {v.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
              </PdfHtml>
            );
          }
        })}
      </View>
    );
  });
  return <>{data}</>;
};
const ContactDetailView: FC<{
  img: string;
  value: string;
  selectedColor?: string;
  isUrl?: boolean;
  title?: string;
  type?: 'email';
  iconWidth?: number;
  iconHeight?: number;
  width?: number;
}> = ({ selectedColor, img, value, isUrl, title, type, iconHeight, iconWidth, width }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.row}>
      <Image
        src={img}
        style={{
          width: iconWidth ?? 17,
          height: iconHeight ?? 17,
          marginTop: 8,
          marginRight: 10,
          color: '#F1FAFF',
          padding: 1,
        }}
      />
      <View style={{ width: width ?? 160, paddingHorizontal: 10, paddingVertical: 8 }}>
        {isUrl ? (
          <Link wrap style={[styles.contactText, { textDecoration: 'none' }]} src={value}>
            {title ?? value}
          </Link>
        ) : type && type === 'email' ? (
          <Link wrap style={[styles.emailText]} src={value}>
            {title ?? value}
          </Link>
        ) : (
          <Text style={styles.contactText}>{value}</Text>
        )}
      </View>
    </View>
  );
};
const ReferenceView: FC<{
  value: References;
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View>
      <Text style={styles.refName}>{Utils.getFullName(value.firstName, value.lastName)}</Text>
      <Text style={styles.text}>{value.designation}</Text>
      <Text style={styles.text}>{value.organization}</Text>
      <View style={{ flexDirection: 'row' }}>
        <Text style={styles.boldText}>Mobile: </Text>
        <Text style={styles.text}>{value.mobile}</Text>
      </View>
      <View style={{ flexDirection: 'row' }}>
        <Text style={styles.boldText}>Email: </Text>
        <Text style={styles.text}>{value.email}</Text>
      </View>
    </View>
  );
};
const Template21 = (props: Props) => {
  const styles = useMemo(() => pageStyles({ color: props.selectedColor }), [props.selectedColor]);
  const { profile } = props;
  const personalStatement = useMemo(() => {
    return `I am on the lookout for a ${profile?.jobType
      ?.join(', ')
      ?.toLowerCase()}, ${profile?.jobCategory?.join(', ')?.toLowerCase()} opportunity as a ${
      profile?.desiredJobTitle?.value
    }${
      [...(profile?.sector ?? [])].length > 0
        ? `, in the ${profile?.sector?.join(', ').replace(/, ([^,]*)$/, ' and $1')} ${
            profile?.sector?.length === 1 ? 'sector' : 'sectors'
          }.`
        : ''
    }`;
    // if (!profile?.personalStatement) {
    //   return `${profile?.jobType ? s1 : ''}`;
    // }
    // return `${profile?.jobType ? s1 : ''} \n ${profile?.personalStatement
    //   ?.replace(/<p><br><\/p>/g, '')
    //   .replace(/em>/g, 'i>')}`;
  }, [profile?.desiredJobTitle, profile?.jobCategory, profile?.jobType, profile?.sector]);

  const isShowActivity = useMemo(() => {
    const temp: Education[] = [...(profile?.education ?? [])];
    const found = temp.find((v) => v.description);
    return !!found;
  }, [profile?.education]);
  const isShowSummery = useMemo(() => {
    return !!(profile?.personalStatement || profile?.desiredJobTitle);
  }, [profile?.desiredJobTitle, profile?.personalStatement]);
  return (
    <Document>
      <Page size={'A4'} break={true} style={styles.body}>
        <View fixed style={styles.sideBar} />
        <View style={styles.headerContent}>
          {/*<View style={styles.redBox} />*/}
          <View style={{ textAlign: 'center', alignItems: 'center' }}>
            <Text style={styles.name}>
              {Utils.getFullName(profile.firstName, profile.lastName)}
            </Text>
            {profile?.personalStatementHeader && (
              <View style={{ marginTop: 5 }}>
                <Text style={styles.designation}>{profile.personalStatementHeader}</Text>
              </View>
            )}
            {profile.country && (
              <View
                style={{
                  marginTop: 5,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <Text style={styles.country}>{profile.country}</Text>
              </View>
            )}
          </View>
          <View style={{ width: 560, borderWidth: 1, marginTop: 10, borderColor: '#707070' }} />

          <View style={{ flexDirection: 'row' }}>
            <View style={{ maxWidth: 400 }}>
              <View style={styles.contacts}>
                {profile?.mobile && (
                  <ContactDetailView
                    value={profile.mobile}
                    img={`${config.frontendUrl}/phone_light.png`}
                    selectedColor={props.selectedColor}
                  />
                )}
                {profile?.residentialAddress && (
                  <ContactDetailView
                    value={profile.residentialAddress}
                    img={`${config.frontendUrl}/locationPin_light.png`}
                    selectedColor={props.selectedColor}
                  />
                )}
                {profile?.linkedInUrl && (
                  <ContactDetailView
                    isUrl={true}
                    title={'Linkedin.com'}
                    value={profile.linkedInUrl}
                    img={`${config.frontendUrl}/linkedin_light.png`}
                    selectedColor={props.selectedColor}
                  />
                )}
                {profile?.userId && (
                  <ContactDetailView
                    isUrl={true}
                    title={'Careers360.com'}
                    value={`${config.frontendUrl}/profile/${profile.userId}`}
                    img={`${config.frontendUrl}/careers360_light.png`}
                    selectedColor={props.selectedColor}
                  />
                )}
              </View>
              <View style={styles.emailContainer}>
                {profile?.email && (
                  <ContactDetailView
                    title={profile.email}
                    type={'email'}
                    value={`mailto:${profile.email}`}
                    img={`${config.frontendUrl}/email_light.png`}
                    selectedColor={props.selectedColor}
                    width={320}
                    iconHeight={15}
                  />
                )}
                {profile?.websiteUrl && profile?.websiteUrl?.length > 0 ? (
                  <View style={{ marginTop: 20, flexDirection: 'row' }}>
                    <Text style={[styles.linkTitle, { marginBottom: 6 }]}>Other Links </Text>
                    {[...(profile?.websiteUrl ?? [])].map((url, index) => (
                      <Link key={index} src={url} style={{ textDecoration: 'none' }}>
                        <Text key={index} style={[styles.linkText]}>
                          &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                          {new URL(url).hostname.replace('www.', '')}
                        </Text>
                      </Link>
                    ))}
                  </View>
                ) : (
                  <></>
                )}
              </View>
            </View>
            <View>
              <View style={{ marginTop: 13, flexDirection: 'row' }}>
                <Text
                  style={[
                    styles.linkTitle,
                    { marginLeft: 2, marginRight: 16, letterSpacing: 0.5 },
                  ]}>
                  All Links
                </Text>
                <View style={{ paddingTop: 5 }}>
                  <Image
                    style={{ width: 80, height: 80 }}
                    source={QRCode.toDataURL(
                      `${config.frontendUrl}/profile/${profile?.userId}/links`,
                    )}
                  />
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <View style={styles.colomn1}>
            <View
              fixed
              render={({ pageNumber }) => {
                if (pageNumber !== 1) {
                  return <View style={{ height: 25 }} />;
                }
                return <View />;
              }}
            />
            <Image src={`${config.frontendUrl}/topBorder.png`} style={styles.topBorderImage} />
            <View>
              {profile?.picture ? (
                <View>
                  <Image
                    style={{
                      width: 150,
                      height: 160,
                      borderRadius: '50%',
                      marginTop: -45,
                      objectFit: 'cover',
                    }}
                    src={profile.picture}
                  />
                  {/*<Image*/}
                  {/*  style={{ width: 150, height: 160, borderRadius: '50%', marginTop: -45 }}*/}
                  {/*  src={`${config.frontendUrl}/New folder/qq.png`}*/}
                  {/*/>*/}
                </View>
              ) : (
                <></>
              )}
              {/*{isShowSummery ? (*/}
              {/*  <View style={{ marginTop: 20 }}>*/}
              {/*    <Text style={styles.title}>Summary</Text>*/}
              {/*    <View style={{ marginTop: 10 }}>*/}
              {/*      <PdfHtml>*/}
              {/*        {personalStatement.jobCategory*/}
              {/*          .replace(/<li><br><\/li>/g, '')*/}
              {/*          .replace(/<p><br><\/p>/g, '')}*/}
              {/*      </PdfHtml>*/}
              {/*      <View style={{ marginTop: 10 }} />*/}
              {/*      {personalStatement.message && <PdfHtml>{personalStatement.message}</PdfHtml>}*/}
              {/*    </View>*/}
              {/*  </View>*/}
              {/*) : (*/}
              {/*  <></>*/}
              {/*)}*/}

              {isShowSummery ? (
                <View style={{ marginTop: 20 }}>
                  <Text style={styles.title}>Summary</Text>
                  <View style={{ marginTop: 10 }}>
                    <PdfHtml fontSize={'12px'}>
                      {personalStatement
                        .replace(/<li><br><\/li>/g, '')
                        .replace(/<p><br><\/p>/g, '')}
                    </PdfHtml>
                    {profile.personalStatement && (
                      <>
                        <View style={{ marginTop: 8 }} />
                        <PdfHtml fontSize={'12px'}>
                          {`${profile?.personalStatement
                            ?.replace(/<p><br><\/p>/g, '')
                            .replace(/em>/g, 'i>')}`}
                        </PdfHtml>
                      </>
                    )}
                  </View>
                </View>
              ) : (
                <></>
              )}

              {/*<View style={{ marginBottom: 10 }}>*/}
              {/*  <Text style={styles.title}>Contact</Text>*/}
              {/*</View>*/}
            </View>
            {profile?.languages && profile?.languages.length > 0 ? (
              <View style={{ paddingTop: 20 }}>
                <View>
                  {profile?.languages.map((lang, index) => (
                    <View key={index} wrap={false}>
                      {index === 0 ? <Text style={styles.title}>Languages</Text> : <></>}
                      <View
                        wrap={false}
                        style={{ flexDirection: 'row', fontSize: 13, marginTop: 16 }}>
                        <Text style={{ color: '#7E7E7E' }}>{'\u2022'}</Text>
                        <Text style={{ flex: 1, paddingLeft: 5, color: '#1E1313' }}>
                          {lang.language} : {lang.fluency}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            ) : (
              <></>
            )}
            {[...(profile.skills ?? [])].length > 0 ? (
              <View style={{ paddingTop: 20, flexDirection: 'row', flexWrap: 'wrap' }}>
                {[...(profile.skills ?? [])].map((skill, index) => (
                  <View wrap={false} key={index} style={{ alignItems: 'flex-start' }}>
                    {index === 0 && (
                      <Text style={[styles.title, { marginBottom: 20, paddingRight: 50 }]}>
                        Skills & Expertise
                      </Text>
                    )}
                    {/*<View wrap={false} key={index} style={styles.tag}>*/}
                    <Text style={[styles.tag, { color: '#01200C', fontSize: 12 }]}>
                      {skill.value}
                    </Text>
                    {/*</View>*/}
                  </View>
                ))}
              </View>
            ) : (
              <></>
            )}

            {isShowActivity ? (
              <View style={{ paddingTop: 30 }}>
                {/*{[...(profile.education ?? [])]*/}
                {/*  .filter((item) => !!item.description)*/}
                {/*  .map((item, index) => (*/}
                {/*    <ActivitySocityView*/}
                {/*      key={index}*/}
                {/*      title={index === 0}*/}
                {/*      value={item}*/}
                {/*      selectedColor={props.selectedColor}*/}
                {/*    />*/}
                {/*  ))}*/}
                <ActivitySocityView
                  value={profile.activities}
                  selectedColor={props.selectedColor}
                />
              </View>
            ) : (
              <></>
            )}
            {profile.volunteering && profile.volunteering.length != 0 ? (
              <View style={{ paddingTop: 22 }}>
                <VolunteeringView
                  value={profile.volunteering}
                  selectedColor={props.selectedColor}
                />
              </View>
            ) : (
              <></>
            )}
            {profile.organizationexp && profile.organizationexp.length != 0 ? (
              <View style={{ paddingTop: 22 }}>
                <OrganizationView
                  value={profile.organizationexp}
                  selectedColor={props.selectedColor}
                />
              </View>
            ) : (
              <></>
            )}
            <View fixed style={{ height: 35 }} />
          </View>
          <View style={styles.colomn2}>
            <View>
              <View
                fixed
                render={({ pageNumber }) => {
                  if (pageNumber !== 1) {
                    return <View style={{ height: 20 }} />;
                  }
                  return <View />;
                }}
              />
              {[...(profile.experience ?? [])].length ? (
                <View>
                  <Text style={styles.title}>Experience</Text>
                  {[...(profile.experience ?? [])].map((item, index) => (
                    <ExpCard key={index} value={item} selectedColor={props.selectedColor} />
                  ))}
                </View>
              ) : (
                <></>
              )}
              {[...(profile.education ?? [])].length > 0 ? (
                <View style={{ paddingTop: 26 }}>
                  {[...(profile.education ?? [])].map((item, index) => (
                    <View key={index} wrap={false}>
                      {index === 0 ? <Text style={styles.title}>Education</Text> : <></>}
                      <EduCard key={index} value={item} selectedColor={props.selectedColor} />
                    </View>
                  ))}
                </View>
              ) : (
                <></>
              )}
              {[...(profile.reference ?? [])].length > 0 ? (
                <View style={{ paddingBottom: 16 }}>
                  {[...(profile.reference ?? [])].map((item, index) => (
                    <View key={index} wrap={false}>
                      {index === 0 ? <Text style={styles.title}>References</Text> : <></>}
                      <ReferenceView key={index} value={item} selectedColor={props.selectedColor} />
                    </View>
                  ))}
                </View>
              ) : (
                <></>
              )}
            </View>
          </View>
          {/*<View fixed style={styles.footerBorder} />*/}
        </View>
        <Text
          fixed
          style={styles.staticContent}
          render={({ pageNumber, totalPages }) => {
            if (pageNumber === totalPages) {
              return profile?.reference ? '' : 'References will be provided upon request';
            }
            return '';
          }}
        />
        {/*<View fixed style={styles.footerShape} />*/}
        {/*<View fixed style={styles.footerBorder} />*/}
        {/*<View fixed style={styles.topBorder} />*/}
        {/*<View*/}
        {/*  fixed*/}
        {/*  render={({ pageNumber }) => {*/}
        {/*    if (pageNumber === 1) {*/}
        {/*      return <View />;*/}
        {/*    }*/}
        {/*    return <View style={styles.topBorder} />;*/}
        {/*  }}*/}
        {/*/>*/}
      </Page>
    </Document>
  );
};
export default Template21;
